import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import ProductModal from "../../components/ProductModal/ProductModal";
import { useLocation } from "react-router-dom";
import InfoDelivery from "../../components/InfoDelivery/InfoDelivery";
import SEO from "../../components/SEO/SEO";

import OrnamentImg from "../../assets/images/ornament.png";
import {
  Wrap,
  Book,
  Spread,
  Page,
  PageInner,
  PageShadow,
  FlipOverlay,
  PageNumber,
  SideButton,
  PeekFlip,
  Case,
  Corner,
  Edge,
  Ribbon,
  IntroWrap,
  IntroTitle,
  IntroList,
  IntroButton,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  ModalClose,
  Ornament,
} from "./MenuPageStyles";

/* ---- GraphQL ---- */
const PRODUCTS_BY_CATEGORIES = gql`
  query ProductsByCategories($categorii: [String!]!) {
    productsByCategories(categorii: $categorii) {
      id
      categorie
      nume
      nume_en
      ingrediente
      ingrediente_en
      alergeni
      alergeni_en
      nutritie
      nutritie_en
      pret
      ordine
      imagine
    }
  }
`;

/* ---- Utils ---- */
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
function buildSpreads(items, perSide = 8, title = "") {
  return chunk(items, perSide * 2).map((batch) => ({
    left: { title, items: batch.slice(0, perSide) },
    right: { title, items: batch.slice(perSide, perSide * 2) },
  }));
}
function buildPagesMobile(items, perPage = 3, title = "") {
  return chunk(items, perPage).map((batch) => ({
    left: { title, items: batch },
    right: null,
  }));
}
function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
function toSlug(s = "") {
  return String(s)
    .normalize?.("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
function useIsMobile(bp = 720) {
  const [mob, setMob] = useState(
    () => window.matchMedia(`(max-width:${bp}px)`).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width:${bp}px)`);
    const onChange = () => setMob(mql.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [bp]);
  return mob;
}

/* ---- FlipBook ---- */
function FlipBook({
  pages,
  onClose,
  onProductClick = () => {},
  currency = "lei",
  t,
}) {
  if (!pages || pages.length === 0) return null;
  const totalSpreads = pages.length;
  const [spreadIndex, setSpreadIndex] = useState(0);
  const isMobile = useIsMobile(720);

  const totalPages = isMobile ? totalSpreads : totalSpreads * 2;

  const [isFlipping, setIsFlipping] = useState(false);
  const flipDirection = useRef("next");
  const canPrev = spreadIndex > 0 && !isFlipping;
  const canNext = spreadIndex < totalSpreads - 1 && !isFlipping;

  const handleNext = () => {
    if (!canNext) return;
    flipDirection.current = "next";
    setIsFlipping(true);
    setTimeout(() => {
      setSpreadIndex((s) => Math.min(s + 1, totalSpreads - 1));
      setIsFlipping(false);
    }, 650);
  };
  const handlePrev = () => {
    if (!canPrev) return;
    flipDirection.current = "prev";
    setIsFlipping(true);
    setTimeout(() => {
      setSpreadIndex((s) => Math.max(s - 1, 0));
      setIsFlipping(false);
    }, 650);
  };

  const turningSide = isFlipping
    ? flipDirection.current === "next"
      ? "right"
      : "left"
    : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const spreadRef = useRef(null);

  // swipe mobil
  const touch = useRef({ x: 0, ignore: false });
  const onTouchStart = (e) => {
    const startTarget = e.target;
    if (startTarget.closest?.(".card")) {
      // dacă pornim atingerea dintr-un card, nu mai flipp-uim pagina
      touch.current.ignore = true;
      return;
    }
    touch.current.ignore = false;
    touch.current.x = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touch.current.ignore) {
      touch.current.ignore = false;
      return;
    }
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 50) return dx < 0 ? handleNext() : handlePrev();
    const rect = spreadRef.current?.getBoundingClientRect();
    const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    e.changedTouches[0].clientX >= centerX ? handleNext() : handlePrev();
  };

  // drag/click desktop
  const mouse = useRef({ x: 0, drag: false, ignore: false });
  const onMouseDown = (e) => {
    if (e.target.closest?.(".card")) {
      // click pornit pe card -> nu pornim drag de pagină
      mouse.current = { x: 0, drag: false, ignore: true };
      return;
    }
    mouse.current = { x: e.clientX, drag: true, ignore: false };
  };
  const onMouseUp = (e) => {
    if (mouse.current.ignore) {
      mouse.current.ignore = false;
      return;
    }
    if (!mouse.current.drag) return;
    const dx = e.clientX - mouse.current.x;
    mouse.current.drag = false;
    if (Math.abs(dx) > 60) return dx < 0 ? handleNext() : handlePrev();
    const rect = spreadRef.current?.getBoundingClientRect();
    const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    e.clientX >= centerX ? handleNext() : handlePrev();
  };
  const onMouseLeave = () => {
    mouse.current.drag = false;
  };

  const current = pages[spreadIndex];

  const renderList = (block) => {
    if (!block) return null;
    const items = Array.isArray(block.items) ? block.items : [];
    return (
      <>
        <h2>{block.title}</h2>
        <ul>
          {items.map((it, i) => (
            <li key={it.id || i}>
              <div
                className={`card${it.img ? "" : " no-thumb"}`}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onProductClick(it);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    onProductClick(it);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {it.img && (
                  <figure
                    className="thumb"
                    aria-label={t("menu.a11y.photoOf", {
                      name: it.name,
                      defaultValue: `${it.name} photo`,
                    })}
                  >
                    <img
                      src={it.img}
                      alt={`${it.name} – ${block.title}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </figure>
                )}
                <div className="meta">
                  <div className="titleRow">
                    <span className="name">{it.name}</span>
                    <span className="dots" />
                    <span className="price">
                      {it.price} {currency}
                    </span>
                  </div>

                  <div className="descWrap">
                    {it.desc && <p className="desc">{it.desc}</p>}
                    <p className="moreHint">{t("menu.actions.moreDetails")}</p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const flipFaces = useMemo(() => {
    if (!isFlipping) return null;
    if (isMobile) {
      const nextSpread = pages[spreadIndex + 1];
      const prevSpread = pages[spreadIndex - 1];
      return flipDirection.current === "next"
        ? {
            side: "mobile",
            front: renderList(current?.left),
            back: nextSpread ? renderList(nextSpread.left) : null,
          }
        : {
            side: "mobile",
            front: renderList(current?.left),
            back: prevSpread ? renderList(prevSpread.left) : null,
          };
    }
    if (flipDirection.current === "next") {
      const nextSpread = pages[spreadIndex + 1];
      return {
        side: "right",
        front: renderList(current?.right),
        back: nextSpread ? renderList(nextSpread.left) : null,
      };
    } else {
      const prevSpread = pages[spreadIndex - 1];
      return {
        side: "left",
        front: renderList(current?.left),
        back: prevSpread ? renderList(prevSpread.right) : null,
      };
    }
  }, [isFlipping, isMobile, spreadIndex, current, pages]);

  const peekBlock = useMemo(() => {
    if (!isFlipping) return null;
    if (isMobile) {
      const target =
        flipDirection.current === "next"
          ? pages[spreadIndex + 1]
          : pages[spreadIndex - 1];
      return target ? { side: "mobile", node: renderList(target.left) } : null;
    }
    if (flipDirection.current === "next") {
      const nextSpread = pages[spreadIndex + 1];
      return nextSpread
        ? { side: "right", node: renderList(nextSpread.right) }
        : null;
    } else {
      const prevSpread = pages[spreadIndex - 1];
      return prevSpread
        ? { side: "left", node: renderList(prevSpread.left) }
        : null;
    }
  }, [isFlipping, isMobile, spreadIndex, pages]);

  return (
    <Book aria-live="polite">
      <SEO
        title="Meniu Delivery - La Fontanella Predeal"
        description="Comandă pizza, paste și meniuri La Fontanella. Livrare rapidă și gratuită în Predeal pentru comenzi de minimum 65 Lei."
      />
      <Case />
      <Corner $pos="tl" />
      <Corner $pos="tr" />
      <Corner $pos="bl" />
      <Corner $pos="br" />
      <Edge $side="left" />
      <Edge $side="right" />
      <Ribbon />

      <SideButton
        $pos="left"
        onClick={handlePrev}
        disabled={!canPrev}
        aria-label={t("menu.a11y.prevPage", "Pagina anterioară")}
      >
        ‹
      </SideButton>
      <SideButton
        $pos="right"
        onClick={handleNext}
        disabled={!canNext}
        aria-label={t("menu.a11y.nextPage", "Pagina următoare")}
      >
        ›
      </SideButton>

      <Spread
        ref={spreadRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <Page $side="left" $hiddenDuringFlip={turningSide === "left"}>
          <PageShadow />
          <PageInner style={{ "--ornament-url": `url(${OrnamentImg})` }}>
            {renderList(current?.left)}
            <PageNumber>
              {isMobile ? spreadIndex + 1 : spreadIndex * 2 + 1}/{totalPages}
            </PageNumber>
          </PageInner>
        </Page>
        <Page $side="right" $hiddenDuringFlip={turningSide === "right"}>
          <PageShadow />
          <PageInner style={{ "--ornament-url": `url(${OrnamentImg})` }}>
            {renderList(current?.right)}
            <PageNumber>
              {isMobile
                ? "" // dacă vrei doar o singură pagină numerotată pe mobil
                : `${Math.min(spreadIndex * 2 + 2, totalPages)}/${totalPages}`}
            </PageNumber>{" "}
          </PageInner>
        </Page>

        {isFlipping && peekBlock && (
          <PeekFlip $side={peekBlock.side} aria-hidden>
            <PageInner style={{ "--ornament-url": `url(${OrnamentImg})` }}>
              {peekBlock.node}
            </PageInner>
          </PeekFlip>
        )}
        {isFlipping && flipFaces && (
          <FlipOverlay $dir={flipDirection.current} $pageSide={flipFaces.side}>
            <div className="face front">
              <PageInner
                aria-hidden
                style={{ "--ornament-url": `url(${OrnamentImg})` }}
              >
                {flipFaces.front}
              </PageInner>
            </div>
            <div className="face back">
              <PageInner
                aria-hidden
                style={{ "--ornament-url": `url(${OrnamentImg})` }}
              >
                {flipFaces.back}
              </PageInner>
            </div>
          </FlipOverlay>
        )}
      </Spread>
    </Book>
  );
}

/* ---- Map cod → etichetă frumoasă ---- */
const CATEGORY_ORDER = [
  "pizza_vatra",
  "pizza_specialitati",
  "pizza_party",
  "meniuri_complete",
  "paste",
  "preparate_gratar",
  "ciorbe_supe",
  "preparate_traditionale",
  "deserturi",
  "garnituri",
  "salate",
  "sosuri",
  "bauturi",
];
const CATEGORY_LABELS_FALLBACK = {
  pizza_vatra: "Pizza pe vatră",
  pizza_specialitati: "Pizza specialități",
  pizza_party: "Pizza Party",
  meniuri_complete: "Meniuri complete",
  paste: "Paste și specialități din paste",
  preparate_gratar: "Preparate la grătar sau ceaun",
  ciorbe_supe: "Ciorbe și supe",
  preparate_traditionale: "Preparate tradiționale",
  deserturi: "Deserturi",
  garnituri: "Garnituri",
  salate: "Salate",
  sosuri: "Sosuri",
  bauturi: "Băuturi răcoritoare și alcoolice",
};

/* ---- Pagina ---- */
export default function MenuPage() {
  const { t, i18n } = useTranslation();
  const isEN = (i18n.language || "").toLowerCase().startsWith("en");
  const currency = t("menu.currency", "lei");

  const [openCat, setOpenCat] = useState(null);
  const isMobile = useIsMobile(720);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // 1) Query params
  const query = useQueryParams();
  const catParam = (query.get("cat") || "").trim();

  // 2) Apollo useQuery (MUSAI înainte de orice folosire a 'data')
  const { data, loading, error } = useQuery(PRODUCTS_BY_CATEGORIES, {
    variables: { categorii: CATEGORY_ORDER },
    fetchPolicy: "cache-first",
  });

  // 3) Derivări din date
  const PER_SIDE_DESKTOP = 3;
  const PER_SIDE_MOBILE = 2;

  const categoryTitle = (code) =>
    t(`menu.categories.${code}`, CATEGORY_LABELS_FALLBACK[code] || code);

  const spreadsByCategory = useMemo(() => {
    const map = {};
    const rows = data?.productsByCategories ?? [];
    for (const r of rows) {
      const cat = r.categorie;
      if (!map[cat]) map[cat] = [];

      const rawIng = isEN ? (r.ingrediente_en ?? r.ingrediente) : r.ingrediente;
      const rawName = isEN ? r.nume_en || r.nume : r.nume;
      const desc = Array.isArray(rawIng) ? rawIng.join(", ") : rawIng || "";

      map[cat].push({
        id: r.id,
        slug: toSlug(rawName),
        name: rawName || "",
        desc,
        ingredients: Array.isArray(rawIng) ? rawIng : rawIng ? [rawIng] : [],
        allergens: isEN ? r.alergeni_en || r.alergeni || "" : r.alergeni || "",
        nutrition: isEN
          ? r.nutritie_en || r.nutritie || null
          : r.nutritie || null,
        price: Number(r.pret).toFixed(0),
        img:
          typeof r.imagine === "string" && r.imagine.length ? r.imagine : null,
      });
    }

    const out = {};
    for (const cat of Object.keys(map)) {
      const title = categoryTitle(cat);
      const perSide = isMobile ? PER_SIDE_MOBILE : PER_SIDE_DESKTOP;
      out[cat] = isMobile
        ? buildPagesMobile(map[cat], perSide, title)
        : buildSpreads(map[cat], perSide, title);
    }
    return out;
  }, [data, isMobile, isEN, t]);

  const categoriesWithData = CATEGORY_ORDER.filter(
    (c) => (spreadsByCategory[c]?.length ?? 0) > 0,
  );

  // 4) Deschide categoria din URL după ce avem date + spreads
  useEffect(() => {
    if (!data) return;
    if (catParam && spreadsByCategory[catParam]?.length > 0) {
      setOpenCat(catParam);
    }
  }, [data, catParam, spreadsByCategory]);

  // Intro pe mobil fără scroll
  const INTRO_PER_PAGE = 7;
  const [introIndex, setIntroIndex] = useState(0);
  useEffect(() => {
    if (!openCat) setIntroIndex(0);
  }, [openCat]);

  const introPages = useMemo(
    () =>
      isMobile
        ? chunk(categoriesWithData, INTRO_PER_PAGE)
        : [categoriesWithData],
    [categoriesWithData, isMobile],
  );
  const canIntroPrev = isMobile && introIndex > 0;
  const canIntroNext =
    isMobile && introIndex < Math.max(introPages.length - 1, 0);

  const introTouch = useRef({ x: 0 });
  const onIntroTouchStart = (e) => {
    if (!isMobile) return;
    introTouch.current.x = e.changedTouches[0].clientX;
  };
  const onIntroTouchEnd = (e) => {
    if (!isMobile) return;
    const dx = e.changedTouches[0].clientX - introTouch.current.x;
    if (Math.abs(dx) > 40) {
      setIntroIndex((i) =>
        dx < 0
          ? Math.min(i + 1, Math.max(introPages.length - 1, 0))
          : Math.max(i - 1, 0),
      );
    }
  };

  return (
    <Wrap style={{ "--ornament-url": `url(${OrnamentImg})` }}>
      {!openCat && (
        <IntroWrap>
          <Book aria-hidden>
            <Case />
            <Corner $pos="tl" />
            <Corner $pos="tr" />
            <Corner $pos="bl" />
            <Corner $pos="br" />
            <Edge $side="left" />
            <Edge $side="right" />
            <Ribbon />

            {isMobile && introPages.length > 1 && (
              <>
                <SideButton
                  $pos="left"
                  onClick={() => setIntroIndex((i) => Math.max(0, i - 1))}
                  disabled={!canIntroPrev}
                  aria-label={t("menu.a11y.prevMenus", "Meniuri anterioare")}
                >
                  ‹
                </SideButton>
                <SideButton
                  $pos="right"
                  onClick={() =>
                    setIntroIndex((i) =>
                      Math.min(Math.max(introPages.length - 1, 0), i + 1),
                    )
                  }
                  disabled={!canIntroNext}
                  aria-label={t("menu.a11y.nextMenus", "Meniuri următoare")}
                >
                  ›
                </SideButton>
              </>
            )}

            <Spread
              onTouchStart={onIntroTouchStart}
              onTouchEnd={onIntroTouchEnd}
            >
              <Page $side="left">
                <PageShadow />
                <PageInner style={{ "--ornament-url": `url(${OrnamentImg})` }}>
                  <IntroTitle>
                    {loading
                      ? t("menu.intro.loading", "Se încarcă meniul...")
                      : error
                        ? t("menu.intro.error", "Eroare la încărcare")
                        : t("menu.intro.pick", "Alege un meniu")}
                  </IntroTitle>
                  <IntroList>
                    {(isMobile
                      ? (introPages[introIndex] ?? [])
                      : categoriesWithData.slice(
                          0,
                          Math.ceil(categoriesWithData.length / 2),
                        )
                    ).map((c) => (
                      <li key={c}>
                        <IntroButton onClick={() => setOpenCat(c)}>
                          {t("menu.intro.seeMenuOf", "Vezi meniul de")}{" "}
                          <strong>{categoryTitle(c)}</strong>
                        </IntroButton>
                      </li>
                    ))}
                  </IntroList>
                </PageInner>
              </Page>

              {!isMobile && (
                <Page $side="right">
                  <PageShadow />
                  <PageInner
                    style={{ "--ornament-url": `url(${OrnamentImg})` }}
                  >
                    <IntroTitle>
                      {t("menu.intro.discover", "Descoperă gusturi")}
                    </IntroTitle>
                    <IntroList>
                      {categoriesWithData
                        .slice(Math.ceil(categoriesWithData.length / 2))
                        .map((c) => (
                          <li key={c}>
                            <IntroButton onClick={() => setOpenCat(c)}>
                              {t("menu.intro.seeMenuOf", "Vezi meniul de")}{" "}
                              <strong>{categoryTitle(c)}</strong>
                            </IntroButton>
                          </li>
                        ))}
                    </IntroList>
                  </PageInner>
                </Page>
              )}
            </Spread>
          </Book>
        </IntroWrap>
      )}

      {openCat && (spreadsByCategory[openCat]?.length ?? 0) > 0 && (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <ModalHeader>
              <ModalTitle aria-hidden />
              <ModalClose
                onClick={() => setOpenCat(null)}
                aria-label={t("menu.actions.close", "Închide")}
              >
                ✕
              </ModalClose>
            </ModalHeader>

            <FlipBook
              pages={spreadsByCategory[openCat]}
              onClose={() => setOpenCat(null)}
              onProductClick={(p) => setSelectedProduct(p)}
              currency={currency}
              t={t}
            />
          </ModalCard>
        </ModalOverlay>
      )}

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </Wrap>
  );
}
